import React, { useEffect, useRef } from 'react';
import { Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import {
  cellBase,
  containerSx,
  DAY_COL,
  FIRST_COL,
  stickyBottom,
  stickyLeft,
  stickyRight,
  stickyTop,
  textWidth,
  TOTAL_COL,
  useFillViewportHeight,
  WEEKEND_COL,
} from './tableStyles';

/**
 * Larghezze delle colonne giorno: la minima prevista (feriale/weekend, o di più con minFor), allargata se un valore non ci sta.
 * collect(consider) chiama consider(day, testo) per ogni valore mostrato (day = numero del giorno, 1-31).
 */
export function measureDayColumns(monthDays, collect, minFor) {
  const widths = monthDays.map((d) => Math.max(d.weekend ? WEEKEND_COL : DAY_COL, minFor?.(d) || 0));
  collect((day, text) => {
    if (text) widths[day - 1] = Math.max(widths[day - 1], textWidth(text));
  });
  return widths;
}

/** Larghezza delle colonne fisse a destra (totali), abbastanza ampia per il testo più lungo. */
export const measureRightColumn = (...texts) => Math.max(TOTAL_COL, ...texts.filter(Boolean).map(textWidth));

/** Stile di una cella fissa a destra: index 0 = la più a sinistra delle count colonne di totale. */
export const rightSx = (index, count, width) => ({ ...stickyRight, right: (count - 1 - index) * width });

/**
 * Base comune delle tabelle mensili V2 (Projects overview, People overview, My month):
 * intestazione dei giorni, colonna del nome fissa a sinistra, colonne dei totali fisse a destra,
 * riga dei totali fissa in basso, righe di caricamento e altezza adattata alla finestra.
 * Le righe del corpo sono della pagina (children): per le celle usano cellBase, stickyLeft e rightSx.
 *
 * rightColumns: [{ key, label, color? }]
 * dayHeaderExtra(d, i): terza riga dell'intestazione del giorno (es. incompleti o assenze)
 * totalRow: { label, cell(d, i) → { content, sx?, title? }, right: [{ content, sx? }] }
 * focusDayIndex: giorno da portare in vista quando la tabella scorre in orizzontale
 */
export default function MonthGrid({
  monthDays,
  firstColLabel,
  firstColWidth = FIRST_COL,
  dayWidths,
  rightColumns,
  rightWidth,
  dayHeaderExtra,
  loading,
  skeletonRows = 4,
  totalRow,
  focusDayIndex,
  fillDeps = [],
  children,
}) {
  const containerRef = useRef(null);
  const maxHeight = useFillViewportHeight(containerRef, [loading, ...fillDeps]);
  const rightCount = rightColumns.length;
  const minWidth = firstColWidth + rightCount * rightWidth + dayWidths.reduce((a, b) => a + b, 0);

  useEffect(() => {
    const el = containerRef.current;
    if (loading || !el || focusDayIndex == null || focusDayIndex < 0) return;
    const left = dayWidths.slice(0, focusDayIndex).reduce((a, b) => a + b, 0);
    const visible = el.clientWidth - firstColWidth - rightCount * rightWidth;
    if (left + dayWidths[focusDayIndex] > el.scrollLeft + visible) el.scrollLeft = Math.max(0, left + dayWidths[focusDayIndex] - visible + 40);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusDayIndex, loading, monthDays]);

  return (
    <TableContainer ref={containerRef} sx={containerSx(maxHeight)}>
      <Table size="small" sx={{ tableLayout: 'fixed', minWidth, width: '100%', borderCollapse: 'separate' }}>
        <colgroup>
          <col style={{ width: firstColWidth }} />
          {dayWidths.map((w, i) => (
            <col key={monthDays[i].day} style={{ width: w }} />
          ))}
          {rightColumns.map((c) => (
            <col key={c.key} style={{ width: rightWidth }} />
          ))}
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...cellBase, ...stickyLeft, ...stickyTop, zIndex: 4, textAlign: 'left', px: 2, fontWeight: 700, color: 'text.secondary' }}>
              {firstColLabel}
            </TableCell>
            {monthDays.map((d, i) => (
              <TableCell
                key={d.day}
                sx={{
                  ...cellBase,
                  ...stickyTop,
                  height: dayHeaderExtra ? 54 : 44,
                  lineHeight: 1.2,
                  color: d.weekend ? 'text.disabled' : 'text.secondary',
                  bgcolor: d.today ? 'var(--rt-today-head)' : d.weekend ? 'var(--rt-grey-50)' : 'background.paper',
                  fontWeight: d.today ? 700 : 500,
                }}
              >
                <div style={{ fontSize: 10 }}>{d.label}</div>
                <div>{d.day}</div>
                {dayHeaderExtra && dayHeaderExtra(d, i)}
              </TableCell>
            ))}
            {rightColumns.map((c, k) => (
              <TableCell key={c.key} sx={{ ...cellBase, ...rightSx(k, rightCount, rightWidth), ...stickyTop, zIndex: 4, fontWeight: 700, color: c.color || 'text.secondary' }}>
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading &&
            Array.from({ length: skeletonRows }).map((_, i) => (
              <TableRow key={`sk-${i}`}>
                <TableCell sx={{ ...cellBase, ...stickyLeft, px: 2 }}>
                  <Skeleton width="70%" />
                </TableCell>
                {monthDays.map((d) => (
                  <TableCell key={d.day} sx={cellBase}>
                    <Skeleton width="60%" sx={{ mx: 'auto' }} />
                  </TableCell>
                ))}
                {rightColumns.map((c, k) => (
                  <TableCell key={c.key} sx={{ ...cellBase, ...rightSx(k, rightCount, rightWidth) }} />
                ))}
              </TableRow>
            ))}

          {!loading && children}

          {!loading && totalRow && (
            <TableRow>
              <TableCell sx={{ ...cellBase, ...stickyLeft, ...stickyBottom, zIndex: 4, textAlign: 'left', px: 2, fontWeight: 700 }}>
                {totalRow.label || 'Total'}
              </TableCell>
              {monthDays.map((d, i) => {
                const cell = totalRow.cell(d, i);
                return (
                  <TableCell
                    key={d.day}
                    title={cell.title}
                    sx={{ ...cellBase, ...stickyBottom, fontWeight: 700, bgcolor: d.weekend ? 'var(--rt-grey-50)' : 'background.paper', ...cell.sx }}
                  >
                    {cell.content}
                  </TableCell>
                );
              })}
              {totalRow.right.map((cell, k) => (
                <TableCell key={rightColumns[k].key} sx={{ ...cellBase, ...rightSx(k, rightCount, rightWidth), ...stickyBottom, zIndex: 4, fontWeight: 800, ...cell.sx }}>
                  {cell.content}
                </TableCell>
              ))}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
